import CITIES from '@/lib/ct2Cities.json';
import { avatarImg } from '@/lib/imgUrl';
// ct2: "<category> · דירוג 4.5+ [G]" — replaces "<plural> מובילים בישראל". Semantic H1 (or p on topic pages) that still carries the category name.
export function GoogleG({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-label="Google" style={{ flexShrink: 0 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// ONE heading style for every section title in the ct2 experts block (1015 Nathan): H1 "<category> · דירוג 4.5+ ★" and the
// "מצאנו X מומחים…" result line share the same pattern — accent bar, bold navy text, " · " separator, gold star on the rating.
export const CT2_SH_CSS = `.ct2-sh-row>p{display:block!important;line-height:1.35}
.ct2-sh-row>p>.ct2-sh{margin:0;padding:0;font-size:inherit!important}
.ct2-sh-row>p>.ct2-sh::before{display:none}
.ct2-sh{display:flex;align-items:center;flex-wrap:wrap;gap:4px 8px}
.ct2-sh,.ct2-sh-row>p{margin:0 0 12px;padding-inline-start:12px;position:relative;font-size:clamp(19px,5.2vw,26px)!important;font-weight:900!important;line-height:1.25;color:#14172a!important;text-align:start}
.ct2-sh::before,.ct2-sh-row>p::before{content:"";position:absolute;inset-inline-start:0;top:.12em;bottom:.12em;width:4px;border-radius:3px;background:linear-gradient(#040154,#ff3f3f)}
.ct2-sh-sep{color:#9aa0bd;font-weight:700}
.ct2-sh-rate{display:inline-flex;align-items:center;gap:6px;color:#040154}
.ct2-sh-star{color:#f5b301;font-size:1em;line-height:1}
.ct2-sh-row{display:block!important;margin-bottom:6px!important}`;

export default function Ct2Rating({ name, as: Tag = 'h1' }: { name: string; as?: 'h1' | 'p' | 'h2' }) {
  return (
    <Tag className="ct2-sh">
      <style>{CT2_SH_CSS}</style>
      <span className="ct2-sh-name">{name}</span>
      <span className="ct2-sh-sep" aria-hidden="true">·</span>
      <span className="ct2-sh-rate" title="דירוג גוגל 4.5 ומעלה">
        <span>דירוג <bdi dir="ltr">4.5+</bdi></span>
        <span className="ct2-sh-star" aria-hidden="true">★</span>
        <GoogleG size={18} />
      </span>
    </Tag>
  );
}

// ct2 (1015 Nathan): ONE compact centered heading "<n> <plural> בארץ|ב<city>" + a city chip ("בחירת עיר ▾" / "<city> ▾").
// The chip's click handler is the page's existing city door (CategoryExpertsList.changeCity → resetCity + experts:open-bar).
export const CT2_HD_CSS = `.ct2-hd{display:flex;flex-direction:row;flex-wrap:wrap;justify-content:center;align-items:center;column-gap:8px;row-gap:6px;text-align:center;margin:0 0 12px;padding-top:4px}
.ct2-hd-t{margin:0;font-size:clamp(20px,5.8vw,27px);font-weight:900;line-height:1.25;color:#14172a}
.ct2-hd-chip{display:inline-flex;align-items:center;justify-content:center;gap:2px;min-width:40px;height:40px;padding:0 6px;margin-inline-start:6px;vertical-align:middle;position:relative;border-radius:999px;border:1.5px solid #c9cdf0;background:#fff;color:#040154;font:inherit;font-size:15px;font-weight:800;cursor:pointer;max-width:100%}
.ct2-hd-chip:active{transform:scale(.98)}
.ct2-hd-chip-c{font-size:12px;line-height:1}
.ct2-hd-chip-l{font-size:15px;font-weight:800;line-height:1;padding-inline:2px 4px;white-space:nowrap}`;

export function Ct2Heading({ count, plural, singular, cityName, as: Tag = 'h1', hasCity, onChip }: {
  count: number; plural: string; singular: string; cityName: string | null; as?: 'h1' | 'p' | 'h2'; hasCity: boolean; onChip: () => void;
}) {
  const where = cityName ? `ב${cityName}` : 'בארץ';
  const cityIcon = hasCity && cityName ? (CITIES as { he: string; icon: string }[]).find(c => c.he === cityName)?.icon || null : null; // Founder 15/10: the chosen city's own icon beside the swap symbol
  return (
    <div className="ct2-hd">
      <style>{CT2_HD_CSS}</style>
      <Tag className="ct2-hd-t">
        {count === 1 ? <span>{singular} {where}</span> : <><bdi dir="ltr">{count}</bdi> <span>{plural} {where}</span></>}
        {/* Founder 15/10: only a swap icon, right after the place word — never the city name again */}
        <button type="button" className="ct2-hd-chip" onClick={onChip} aria-label={hasCity && cityName ? `החלפת עיר (עכשיו: ${cityName})` : 'בחירת עיר'}>{cityIcon && <img {...avatarImg(cityIcon, 28)} alt="" aria-hidden="true" width={28} height={28} style={{ width: 28, height: 28, objectFit: 'contain', display: 'block' }} />}<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 11A8 8 0 0 0 5.1 7.5M4 3.5v4h4M4 13a8 8 0 0 0 14.9 3.5M20 20.5v-4h-4" /></svg>{!cityIcon && <span className="ct2-hd-chip-l">בחירת עיר</span>/* Founder 15/10: no city chosen (בארץ) → say it */}<span aria-hidden="true" style={{ position: 'absolute', inset: -2 }} /></button>
      </Tag>
    </div>
  );
}
